import React from 'react';

/**
 * LoteTabGeneral - General tab content for lot details
 */
const LoteTabGeneral = ({ lote }) => {
    return (
        <div className="space-y-2">
            {/* Escritorio con logo */}
            <div className="bg-gray-50 rounded p-3 border border-gray-200">
                <p className="text-xs text-gray-600 font-semibold mb-2">Escritorio</p>
                <div className="flex items-center gap-3">
                    {lote.escritorio_logo ? (
                        <img
                            src={`https://plazarural.com.uy/uploads/escritorios/${lote.escritorio_logo}`}
                            alt={lote.escritorio}
                            className="h-10 object-contain filter brightness-0 invert-[0.2]"
                        />
                    ) : null}
                    <p className="text-sm font-bold text-gray-900">{lote.escritorio}</p>
                </div>
            </div>

            {/* Ovinos vs Vacunos content */}
            {lote.tipo_ganado === 'L' ? (
                <>
                    {/* Condición Corporal for Ovinos */}
                    <div className="bg-purple-50 rounded p-2 border border-purple-200">
                        <p className="text-xs text-purple-800 font-semibold mb-2">Condición Corporal</p>
                        <div className="grid grid-cols-3 gap-2">
                            <div className="bg-white rounded p-2 border border-purple-300 text-center">
                                <p className="text-[10px] text-purple-700 font-semibold mb-1">Mínimo</p>
                                <p className="font-black text-purple-900 text-lg">{lote.condicion_corporal_min || 'N/A'}</p>
                            </div>
                            <div className="bg-white rounded p-2 border border-purple-300 text-center">
                                <p className="text-[10px] text-purple-700 font-semibold mb-1">Máximo</p>
                                <p className="font-black text-purple-900 text-lg">{lote.condicion_corporal_max || 'N/A'}</p>
                            </div>
                            <div className="bg-white rounded p-2 border border-purple-300 text-center">
                                <p className="text-[10px] text-purple-700 font-semibold mb-1">Promedio</p>
                                <p className="font-black text-purple-900 text-lg">{lote.condicion_corporal || 'N/A'}</p>
                            </div>
                        </div>
                    </div>

                    {/* Razas + Edad for Ovinos */}
                    <div className="grid grid-cols-2 gap-2">
                        <div className="bg-gray-50 rounded p-2 border border-gray-200">
                            <p className="text-xs text-gray-600 font-semibold mb-1">Razas</p>
                            <p className="text-sm font-bold text-gray-900">{lote.raza_detallada}</p>
                        </div>
                        <div className="bg-gray-50 rounded p-2 border border-gray-200">
                            <p className="text-xs text-gray-600 font-semibold mb-1">Edades</p>
                            <p className="text-sm font-bold text-gray-900">{lote.edad || 'N/A'}</p>
                        </div>
                    </div>

                    {/* Ovinos Information Section */}
                    <div className="bg-purple-50 rounded p-2 border border-purple-200">
                        <p className="text-xs text-purple-800 font-semibold mb-2">Información del Lote</p>
                        <div className="grid grid-cols-2 gap-2">
                            <div className="bg-white rounded p-2 border border-purple-300">
                                <p className="text-[10px] text-purple-700 font-semibold mb-1">Registro</p>
                                <p className="font-black text-purple-900 text-sm">{lote.registro || 'N/A'}</p>
                            </div>
                            <div className="bg-white rounded p-2 border border-purple-300">
                                <p className="text-[10px] text-purple-700 font-semibold mb-1">Nivel</p>
                                <p className="font-black text-purple-900 text-sm">{lote.nivel || 'N/A'}</p>
                            </div>
                            <div className="bg-white rounded p-2 border border-purple-300">
                                <p className="text-[10px] text-purple-700 font-semibold mb-1">Machos</p>
                                <p className="font-black text-purple-900 text-sm">{lote.machos ?? 'N/A'}</p>
                            </div>
                            <div className="bg-white rounded p-2 border border-purple-300">
                                <p className="text-[10px] text-purple-700 font-semibold mb-1">Hembras</p>
                                <p className="font-black text-purple-900 text-sm">{lote.hembras ?? 'N/A'}</p>
                            </div>
                        </div>
                    </div>

                    {/* Identificación */}
                    {lote.identificacion && (
                        <div className="bg-gray-50 rounded p-2 border border-gray-200">
                            <p className="text-xs text-gray-600 font-semibold mb-1">Identificación</p>
                            <p className="text-sm font-bold text-gray-900">{lote.identificacion}</p>
                        </div>
                    )}

                    {/* Tratamientos */}
                    <div className="bg-green-50 rounded p-2 border border-green-200">
                        <p className="text-xs text-green-800 font-semibold mb-2">Tratamientos y Manejo</p>
                        <div className="space-y-2">
                            <div className="bg-white rounded p-2 border border-green-300">
                                <p className="text-[10px] text-green-700 font-semibold mb-1">Trat. Endoparasitario</p>
                                <p className="font-bold text-green-900 text-sm">{lote.tratamiento_endop || 'N/A'}</p>
                            </div>
                            <div className="bg-white rounded p-2 border border-green-300">
                                <p className="text-[10px] text-green-700 font-semibold mb-1">Trat. Ectoparasitario</p>
                                <p className="font-bold text-green-900 text-sm">{lote.tratamiento_ectop || 'N/A'}</p>
                            </div>
                            <div className="bg-white rounded p-2 border border-green-300">
                                <p className="text-[10px] text-green-700 font-semibold mb-1">Manejo Pastura</p>
                                <p className="font-bold text-green-900 text-sm">{lote.manejo_pastura || 'N/A'}</p>
                            </div>
                        </div>
                    </div>

                    {/* Esquila */}
                    <div className="grid grid-cols-2 gap-2">
                        <div className="bg-gray-50 rounded p-2 border border-gray-200">
                            <p className="text-xs text-gray-600 font-semibold mb-1">Fecha Esquila</p>
                            <p className="text-sm font-bold text-gray-900">{lote.fecha_esquila || 'N/A'}</p>
                        </div>
                        <div className="bg-gray-50 rounded p-2 border border-gray-200">
                            <p className="text-xs text-gray-600 font-semibold mb-1">Tipo Esquila</p>
                            <p className="text-sm font-bold text-gray-900">
                                {lote.tipo_esquila === 'N' ? 'Normal' : lote.tipo_esquila === 'T' ? 'Temprana' : lote.tipo_esquila || 'N/A'}
                            </p>
                        </div>
                    </div>

                    {/* Flags */}
                    <div className="grid grid-cols-3 gap-2">
                        <div className="bg-gray-50 rounded p-2 border border-gray-200 text-center">
                            <p className="text-[10px] text-gray-600 font-semibold mb-1">Flechilla</p>
                            <p className={`font-black text-sm ${lote.flechilla ? 'text-red-600' : 'text-green-600'}`}>
                                {lote.flechilla ? 'SÍ' : 'NO'}
                            </p>
                        </div>
                        <div className="bg-gray-50 rounded p-2 border border-gray-200 text-center">
                            <p className="text-[10px] text-gray-600 font-semibold mb-1">Nº Señales Liq.</p>
                            <p className="font-black text-gray-900 text-sm">{lote.numero_senales ?? 'N/A'}</p>
                        </div>
                        <div className="bg-gray-50 rounded p-2 border border-gray-200 text-center">
                            <p className="text-[10px] text-gray-600 font-semibold mb-1">Destete</p>
                            <p className={`font-black text-sm ${lote.destete ? 'text-green-600' : 'text-red-600'}`}>
                                {lote.destete ? 'SÍ' : 'NO'}
                            </p>
                        </div>
                    </div>

                    {/* Vacunaciones */}
                    <div className="grid grid-cols-2 gap-2">
                        <div className="bg-blue-50 rounded p-2 border border-blue-200 text-center">
                            <p className="text-[10px] text-blue-700 font-semibold mb-1">VC Ectima</p>
                            <p className={`font-black text-sm ${lote.vc_ectima ? 'text-green-600' : 'text-red-600'}`}>
                                {lote.vc_ectima ? 'SÍ' : 'NO'}
                            </p>
                        </div>
                        <div className="bg-blue-50 rounded p-2 border border-blue-200 text-center">
                            <p className="text-[10px] text-blue-700 font-semibold mb-1">VC Clostridiosis</p>
                            <p className={`font-black text-sm ${lote.vc_clostri ? 'text-green-600' : 'text-red-600'}`}>
                                {lote.vc_clostri ? 'SÍ' : 'NO'}
                            </p>
                        </div>
                    </div>
                </>
            ) : (
                <>
                    {/* Vacunos/Toros content */}
                    <div className="grid grid-cols-4 gap-2">
                        <div className="bg-blue-50 rounded p-2 border border-blue-200 text-center">
                            <p className="text-[10px] text-blue-700 font-semibold mb-1">MIOMIO</p>
                            <p className="font-black text-blue-900 text-sm">{lote.mio}</p>
                        </div>
                        <div className="bg-blue-50 rounded p-2 border border-blue-200 text-center">
                            <p className="text-[10px] text-blue-700 font-semibold mb-1">Z.Gar</p>
                            <p className="font-black text-blue-900 text-sm">{lote.z_gar}</p>
                        </div>
                        <div className="bg-blue-50 rounded p-2 border border-blue-200 text-center">
                            <p className="text-[10px] text-blue-700 font-semibold mb-1">Ración</p>
                            <p className="font-black text-blue-900 text-sm">{lote.saben_comer_racion}</p>
                        </div>
                        <div className="bg-gray-50 rounded p-2 border border-gray-200 text-center">
                            <p className="text-[10px] text-gray-600 font-semibold mb-1">Edad</p>
                            <p className="font-bold text-gray-900 text-xs">
                                {lote.edad?.replace(/meses/gi, 'M').replace(/años/gi, 'A').replace(/año/gi, 'A').replace(/mes/gi, 'M')}
                            </p>
                        </div>
                    </div>

                    {/* Razas */}
                    <div className="bg-gray-50 rounded p-2 border border-gray-200">
                        <p className="text-xs text-gray-600 font-semibold mb-1">Razas</p>
                        <p className="text-sm font-bold text-gray-900">{lote.raza_detallada}</p>
                    </div>

                    {/* Cantidades */}
                    <div className="bg-gray-50 rounded p-2 border border-gray-200">
                        <p className="text-xs text-gray-600 font-semibold mb-2">Cantidades</p>
                        <div className="grid grid-cols-4 gap-2">
                            <div className="bg-white rounded p-2 border border-gray-300 text-center">
                                <p className="text-[10px] text-gray-600 font-semibold mb-1">Destetados</p>
                                <p className="font-black text-gray-900 text-sm">{lote.destetados || 0}</p>
                            </div>
                            <div className="bg-white rounded p-2 border border-gray-300 text-center">
                                <p className="text-[10px] text-gray-600 font-semibold mb-1">Al Pie</p>
                                <p className="font-black text-gray-900 text-sm">{lote.al_pie || 0}</p>
                            </div>
                            <div className="bg-white rounded p-2 border border-gray-300 text-center">
                                <p className="text-[10px] text-gray-600 font-semibold mb-1">Castrados</p>
                                <p className="font-black text-gray-900 text-sm">{lote.castrados || 0}</p>
                            </div>
                            <div className="bg-white rounded p-2 border border-gray-300 text-center">
                                <p className="text-[10px] text-gray-600 font-semibold mb-1">Enteros</p>
                                <p className="font-black text-gray-900 text-sm">{lote.enteros || 0}</p>
                            </div>
                            <div className="bg-white rounded p-2 border border-gray-300 text-center">
                                <p className="text-[10px] text-gray-600 font-semibold mb-1">Mochos</p>
                                <p className="font-black text-gray-900 text-sm">{lote.mochos || 0}</p>
                            </div>
                            <div className="bg-white rounded p-2 border border-gray-300 text-center">
                                <p className="text-[10px] text-gray-600 font-semibold mb-1">Astados</p>
                                <p className="font-black text-gray-900 text-sm">{lote.astados || 0}</p>
                            </div>
                            <div className="bg-white rounded p-2 border border-gray-300 text-center">
                                <p className="text-[10px] text-gray-600 font-semibold mb-1">Mochados</p>
                                <p className="font-black text-gray-900 text-sm">{lote.mochados || 0}</p>
                            </div>
                            <div className="bg-white rounded p-2 border border-gray-300 text-center">
                                <p className="text-[10px] text-gray-600 font-semibold mb-1">Tocos</p>
                                <p className="font-black text-gray-900 text-sm">{lote.tocos || 0}</p>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Vendedor, Establecimiento y Trat. Nutricional */}
            <div className={`grid ${lote.tipo_ganado === 'L' ? 'grid-cols-2' : 'grid-cols-3'} gap-2`}>
                <div className="bg-blue-50 rounded p-2 border border-blue-200">
                    <p className="text-xs text-blue-700 font-semibold mb-1">Vendedor</p>
                    <p className="text-sm font-bold text-blue-900">{lote.vendedor_nombre}</p>
                </div>
                <div className="bg-blue-50 rounded p-2 border border-blue-200">
                    <p className="text-xs text-blue-700 font-semibold mb-1">Establecimiento</p>
                    <p className="text-sm font-bold text-blue-900">{lote.establecimiento}</p>
                </div>
                {lote.tipo_ganado !== 'L' && (
                    <div className="bg-blue-50 rounded p-2 border border-blue-200">
                        <p className="text-xs text-blue-700 font-semibold mb-1">Trat. Nutricional</p>
                        <p className="text-sm font-bold text-blue-900">{lote.tratamiento_nutricional}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LoteTabGeneral;
